import SplitShipmentPdfButton from "components/Pdfs/SplitShipmentPdf/SplitShipmentPdfButton";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import SplitAddressModal from "./SplitAddressModal";
import { SPLIT_STATUSES, addressLabel, parseAddress } from "./splitAddress";

/**
 * Work order -> Split Shipments tab (user, 2026-11-04).
 *
 * Top: one row per product size, one column per split; type how many of each
 * size go in each split. "Left" shows what is not in any split yet.
 * Below: one card per split -- its address, carrier, tracking, status, ship
 * date, notes and its own packing-slip PDF. One Save writes everything; the
 * API refuses a save that would split more than the WO ordered.
 */

const qty = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
const cellKey = (pid, sizeKey) => `${pid}|${sizeKey}`;
const MAX_SPLITS = 20;

const fromServer = (s) => ({
  id: s.id,
  splitId: s.splitId,
  shippingAddress: parseAddress(s.shippingAddress),
  carrier: s.carrier || "",
  trackingNo: s.trackingNo || "",
  status: s.status || "pending",
  notes: s.notes || "",
  shipDate: s.shipDate || "",
  alloc: Object.fromEntries(
    (s.items || []).map((i) => [cellKey(i.woProductId, i.sizeKey), i.qty]),
  ),
});

const blankSplit = (address) => ({
  id: null,
  splitId: null,
  shippingAddress: address,
  carrier: "",
  trackingNo: "",
  status: "pending",
  notes: "",
  shipDate: "",
  alloc: {},
});

export default function WoSplitShipments({ selectedWorkOrder }) {
  const dispatch = useDispatch();
  const woId = selectedWorkOrder?.woId;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // { workOrder, customer, products }
  const [splits, setSplits] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [addressFor, setAddressFor] = useState(null); // split index

  const notify = (message, type = "error") =>
    dispatch(setNotification({ message, type }));
  const apiMessage = (err) =>
    err?.response?.data?.message || err?.message || "Something went wrong";

  const load = useCallback(async () => {
    if (!woId) return;
    setLoading(true);
    setError("");
    try {
      const { data: res } = await API.get(`/split-shipments/wo/${woId}`, {
        headers: authHeader(),
      });
      if (!res?.success) throw new Error(res?.message);
      setData(res.data);
      setSplits(res.data.splits.map(fromServer));
      setDeletedIds([]);
      setDirty(false);
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setLoading(false);
    }
  }, [woId]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(
    () =>
      (data?.products || []).flatMap((p) =>
        p.sizes.map((z) => ({ product: p, size: z, key: cellKey(p.id, z.key) })),
      ),
    [data],
  );

  const splitTotal = (rowKey) =>
    splits.reduce((sum, s) => sum + qty(s.alloc[rowKey]), 0);

  const change = (i, patch) => {
    setSplits((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
    setDirty(true);
  };

  const setCell = (i, rowKey, value) =>
    change(i, { alloc: { ...splits[i].alloc, [rowKey]: value } });

  const fillRest = (i) => {
    const alloc = { ...splits[i].alloc };
    rows.forEach((r) => {
      const others = splits.reduce(
        (sum, s, j) => (j === i ? sum : sum + qty(s.alloc[r.key])),
        0,
      );
      alloc[r.key] = Math.max(r.size.ordered - others, 0) || "";
    });
    change(i, { alloc });
  };

  const addSplit = () => {
    // The first split defaults to the WO's own ship-to; later ones start
    // empty and open the address picker.
    const woAddress = parseAddress(data?.workOrder?.shippingAddress);
    const first = splits.length === 0 && woAddress;
    setSplits((prev) => [...prev, blankSplit(first ? woAddress : null)]);
    setDirty(true);
    if (!first) setAddressFor(splits.length);
  };

  // "How many splits?" (user, 2026-11-05): create them all at once. The
  // first uses the WO's ship-to; the rest show "Add address" on their card.
  const [splitCount, setSplitCount] = useState(2);
  const createSplits = (e) => {
    e.preventDefault();
    const n = Math.min(Math.max(qty(splitCount), 1), MAX_SPLITS);
    const woAddress = parseAddress(data?.workOrder?.shippingAddress);
    setSplits(
      Array.from({ length: n }, (_, i) => blankSplit(i === 0 ? woAddress : null)),
    );
    setDirty(true);
  };

  const removeSplit = (i) => {
    const s = splits[i];
    const label = s.splitId ? `SP-${s.splitId}` : "this new split";
    if (!window.confirm(`Remove ${label}? It is deleted when you Save.`)) return;
    if (s.id) setDeletedIds((d) => [...d, s.id]);
    setSplits((prev) => prev.filter((_, j) => j !== i));
    setDirty(true);
  };

  const overRows = rows.filter((r) => splitTotal(r.key) > r.size.ordered);
  const emptySplits = splits.filter(
    (s) => !Object.values(s.alloc).some((v) => qty(v) > 0),
  );
  const noAddress = splits.filter((s) => !s.shippingAddress);
  const canSave =
    dirty &&
    !loading &&
    overRows.length === 0 &&
    emptySplits.length === 0 &&
    noAddress.length === 0;

  const save = async () => {
    setLoading(true);
    setError("");
    try {
      const body = {
        deletedIds,
        splits: splits.map((s) => {
          const items = {};
          Object.entries(s.alloc).forEach(([k, v]) => {
            if (!qty(v)) return;
            const [pid, ...rest] = k.split("|");
            (items[pid] = items[pid] || {})[rest.join("|")] = qty(v);
          });
          return {
            id: s.id || undefined,
            shippingAddress: s.shippingAddress,
            carrier: s.carrier,
            trackingNo: s.trackingNo,
            status: s.status,
            notes: s.notes,
            shipDate: s.shipDate || null,
            items,
          };
        }),
      };
      const { data: res } = await API.put(`/split-shipments/wo/${woId}`, body, {
        headers: authHeader(),
      });
      if (!res?.success) throw new Error(res?.message);
      notify("Split shipments saved", "success");
      await load();
    } catch (err) {
      // The API's own reason, e.g. which size would be split past ordered.
      setError(apiMessage(err));
      setLoading(false);
    }
  };

  // The slip shows the split as it is on screen; unsaved changes are flagged.
  const pdfSplit = (s) => ({
    ...s,
    items: Object.entries(s.alloc)
      .filter(([, v]) => qty(v) > 0)
      .map(([k, v]) => {
        const [pid, ...rest] = k.split("|");
        const sizeKey = rest.join("|");
        const row = rows.find((r) => r.key === k);
        return { woProductId: Number(pid), sizeKey, size: row?.size.size, qty: qty(v) };
      }),
  });

  if (!woId) return null;

  return (
    <div className="bg-white rounded p-3 position-relative">
      {loading && <Spinner />}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h4 className="mb-0">Split Shipments</h4>
          <small className="text-muted">
            Send this order to several addresses. Each split gets its own
            packing slip.
          </small>
        </div>
        <div className="d-flex gap-2">
          {dirty && (
            <button className="btn btn-outline-secondary" onClick={load}>
              Discard changes
            </button>
          )}
          <button className="btn btn-outline-primary" onClick={addSplit}>
            <i className="mdi mdi-plus" /> Add split
          </button>
          <button className="btn btn-success" disabled={!canSave} onClick={save}>
            Save
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {dirty && (
        <div className="small text-warning mb-2">
          Unsaved changes.
          {overRows.length > 0 && (
            <span className="text-danger ms-2">
              {overRows.length} size(s) split past what was ordered.
            </span>
          )}
          {emptySplits.length > 0 && (
            <span className="text-danger ms-2">
              {emptySplits.length} split(s) have no items.
            </span>
          )}
          {noAddress.length > 0 && (
            <span className="text-danger ms-2">
              {noAddress.length} split(s) have no address.
            </span>
          )}
        </div>
      )}

      {data && rows.length === 0 && (
        <div className="alert alert-info">
          This work order has no products with quantities to split.
        </div>
      )}

      {rows.length > 0 && splits.length === 0 && !loading && (
        <form
          onSubmit={createSplits}
          className="alert alert-light border d-flex flex-wrap align-items-center gap-2"
        >
          <label htmlFor="split-count" className="mb-0 fw-semibold text-dark">
            How many splits?
          </label>
          <input
            id="split-count"
            type="number"
            min={1}
            max={MAX_SPLITS}
            value={splitCount}
            onChange={(e) => setSplitCount(e.target.value)}
            className="form-control form-control-sm"
            style={{ width: 80 }}
            autoFocus
          />
          <button type="submit" className="btn btn-primary btn-sm" disabled={!qty(splitCount)}>
            Create splits
          </button>
          <small className="text-muted">
            The first split uses this work order's ship-to address; set an
            address on each of the others.
          </small>
        </form>
      )}

      {rows.length > 0 && splits.length > 0 && (
        // The WO page itself does not scroll; each tab scrolls in its own
        // box (W-Products does the same). The header + Save stay on top.
        // (user, 2026-11-04: "can't scroll down")
        <div
          style={{ maxHeight: "calc(100vh - 320px)", overflowY: "auto" }}
          className="pe-1"
        >
          <div className="table-responsive mb-3">
            <table className="table table-sm table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: 220 }}>Product / Size</th>
                  <th className="text-end">Ordered</th>
                  {splits.map((s, i) => (
                    <th key={s.id || `new-${i}`} style={{ minWidth: 120 }}>
                      <div className="d-flex justify-content-between">
                        <span>{s.splitId ? `SP-${s.splitId}` : "New split"}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          title="Put everything not yet split here"
                          onClick={() => fillRest(i)}
                        >
                          Fill rest
                        </button>
                      </div>
                      <div
                        className="small fw-normal text-muted text-truncate"
                        style={{ maxWidth: 160 }}
                        title={addressLabel(s.shippingAddress)}
                      >
                        {s.shippingAddress?.city
                          ? `${s.shippingAddress.city}, ${s.shippingAddress.state || ""}`
                          : addressLabel(s.shippingAddress)}
                      </div>
                    </th>
                  ))}
                  <th className="text-end">Left</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const left = r.size.ordered - splitTotal(r.key);
                  return (
                    <tr key={r.key}>
                      <td>
                        {[r.product.productStyle, r.product.productColor]
                          .filter(Boolean)
                          .join(" / ") || r.product.productType}
                        <span className="badge bg-secondary ms-2">
                          {r.size.size}
                        </span>
                      </td>
                      <td className="text-end">{r.size.ordered}</td>
                      {splits.map((s, i) => (
                        <td key={s.id || `new-${i}`}>
                          <input
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                            value={s.alloc[r.key] ?? ""}
                            onChange={(e) => setCell(i, r.key, e.target.value)}
                          />
                        </td>
                      ))}
                      <td
                        className={`text-end fw-bold ${
                          left < 0 ? "text-danger" : left === 0 ? "text-success" : ""
                        }`}
                      >
                        {left}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="row g-3">
            {splits.map((s, i) => {
              const total = Object.values(s.alloc).reduce((a, v) => a + qty(v), 0);
              return (
                <div className="col-xl-4 col-md-6" key={s.id || `new-${i}`}>
                  <div className="border rounded p-2 h-100">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>
                        {s.splitId ? `SP-${s.splitId}` : "New split"}
                        <span className="text-muted fw-normal ms-2">
                          {total} pcs
                        </span>
                      </strong>
                      <div className="d-flex gap-1">
                        {data && (
                          <SplitShipmentPdfButton
                            split={pdfSplit(s)}
                            workOrder={data.workOrder}
                            products={data.products}
                            customer={data.customer}
                            disabled={total === 0}
                          />
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          title="Remove split"
                          onClick={() => removeSplit(i)}
                        >
                          <i className="mdi mdi-delete" />
                        </button>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-start border rounded bg-light p-2 mb-2">
                      <small className={s.shippingAddress ? "" : "text-danger"}>
                        {addressLabel(s.shippingAddress)}
                      </small>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary py-0 ms-2 text-nowrap"
                        onClick={() => setAddressFor(i)}
                      >
                        {s.shippingAddress ? "Change" : "Add address"}
                      </button>
                    </div>

                    <div className="row g-1">
                      <div className="col-6">
                        <input
                          className="form-control form-control-sm"
                          placeholder="Carrier"
                          aria-label="Carrier"
                          value={s.carrier}
                          onChange={(e) => change(i, { carrier: e.target.value })}
                        />
                      </div>
                      <div className="col-6">
                        <input
                          className="form-control form-control-sm"
                          placeholder="Tracking number"
                          aria-label="Tracking number"
                          value={s.trackingNo}
                          onChange={(e) => change(i, { trackingNo: e.target.value })}
                        />
                      </div>
                      <div className="col-6">
                        <select
                          className="form-select form-select-sm"
                          aria-label="Status"
                          value={s.status}
                          onChange={(e) => change(i, { status: e.target.value })}
                        >
                          {SPLIT_STATUSES.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-6">
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          aria-label="Ship date"
                          value={s.shipDate || ""}
                          onChange={(e) => change(i, { shipDate: e.target.value })}
                        />
                      </div>
                      <div className="col-12">
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          placeholder="Notes (printed on the packing slip)"
                          aria-label="Notes"
                          value={s.notes}
                          onChange={(e) => change(i, { notes: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <SplitAddressModal
        show={addressFor !== null}
        onHide={() => setAddressFor(null)}
        initial={addressFor !== null ? splits[addressFor]?.shippingAddress : null}
        customerId={selectedWorkOrder?.customerId}
        title={
          addressFor !== null && splits[addressFor]?.splitId
            ? `Ship-to for SP-${splits[addressFor].splitId}`
            : "Ship-to for the new split"
        }
        onSelect={(address) => {
          change(addressFor, { shippingAddress: address });
          setAddressFor(null);
        }}
      />
    </div>
  );
}
